import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import type { MenuProps } from "antd";
import { Menu as AntDMenu } from "antd";
import { useIntl } from "react-intl";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";

import configService from "@app/config/ConfigService";
import localSettings from "@app/config/LocalSettings";
import Path from "@app/enum/Path";
import settingsAnalytics from "@app/scene/settings/settingsAnaytics";
import useMenu from "@app/scene/settings/util/useMenu";
import authService from "@app/store/auth/AuthService";
import {
  doesUserHaveAccessToDupeCheck,
  doesUserHaveAccessToImports,
  isCurrentUserManager,
  isCurrentUserOwner,
  isCurrentUserReadOnly,
} from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { getLegacyAppUrl } from "@app/util/appUrl";

import getItem from "./getItem";
import styles from "./Menu.module.scss";

const MENU_KEY = "settings_menu";

interface Props {
  doesUserHaveAccessToDupeCheck: boolean;
  doesUserHaveAccessToImports: boolean;
  isManager: boolean;
  isOwner: boolean;
  isReadOnlyMember: boolean;
  onClick?: () => void;
}

const Menu: React.FC<Props> = ({
  doesUserHaveAccessToDupeCheck,
  doesUserHaveAccessToImports,
  isManager,
  isOwner,
  isReadOnlyMember,
  onClick,
}) => {
  const intl = useIntl();
  const menuCategories = useMenu(
    intl,
    isOwner,
    isManager,
    isReadOnlyMember,
    doesUserHaveAccessToDupeCheck,
    doesUserHaveAccessToImports
  );
  const { pathname } = useLocation();

  const [activeKeys, setActiveKeys] = useState<string[] | undefined>(() => {
    const savedKeys = localSettings.getCollapseMenuStateByKey(MENU_KEY);
    return (savedKeys ?? []).length > 0 ? savedKeys : [menuCategories[0].name];
  });

  const handleChangeMenu = useCallback(
    (value: string[]) => {
      setActiveKeys(value);
      localSettings.setCollapseMenuStateByKey(MENU_KEY, value);
    },
    [setActiveKeys]
  );

  const baseOldAppUrl = configService.getBaseOldAppUrl();
  const token = authService.getToken();

  const items: MenuProps["items"] = useMemo(
    () =>
      menuCategories.map(({ icon, items, name, uid }) =>
        getItem(
          name,
          uid,
          icon,
          (items ?? []).map(({ globalPath, key, label, legacyPath, path, uid }) => {
            if (legacyPath) {
              return getItem(
                <a
                  className={styles.link}
                  href={getLegacyAppUrl(baseOldAppUrl, token, legacyPath)}
                  key={legacyPath}
                >
                  {label}
                </a>,
                uid
              );
            }

            return getItem(
              <Link
                className={styles.link}
                key={path}
                onClick={() => {
                  settingsAnalytics?.clicked([key]);
                  onClick?.();
                }}
                to={globalPath ?? `${Path.SETTINGS}/${path}`}
              >
                {label}
              </Link>,
              uid
            );
          })
        )
      ),
    [baseOldAppUrl, menuCategories, onClick, token]
  );

  if (!token) {
    return null;
  }

  return (
    <div className={styles.container}>
      <AntDMenu
        className={styles.menu}
        defaultOpenKeys={activeKeys}
        defaultSelectedKeys={[pathname.replace(`${Path.SETTINGS}/`, "")]}
        items={items}
        mode="inline"
        onOpenChange={handleChangeMenu}
        theme="light"
      />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  doesUserHaveAccessToDupeCheck: doesUserHaveAccessToDupeCheck(state),
  doesUserHaveAccessToImports: doesUserHaveAccessToImports(state),
  isManager: isCurrentUserManager(state),
  isOwner: isCurrentUserOwner(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
});

export default connect(mapStateToProps)(Menu);
